(() => {
  if (customElements.get('edifier-retailer-button')) return;

  class EdifierRetailerButton extends HTMLElement {
    static validateUrl(value) {
      if (typeof value !== 'string' || value.trim() === '') return null;

      try {
        const url = new URL(value);
        const allowedHosts = new Set(['jbhifi.com.au', 'www.jbhifi.com.au']);

        if (
          url.protocol !== 'https:' ||
          !allowedHosts.has(url.hostname.toLowerCase()) ||
          url.port !== '' ||
          url.username !== '' ||
          url.password !== ''
        ) {
          return null;
        }

        return url.href;
      } catch (_error) {
        return null;
      }
    }

    static resolveDestination(data, variantId) {
      const variants = Array.isArray(data?.variants) ? data.variants : [];
      const selectedVariant = variants.find((variant) => String(variant.id) === String(variantId));
      const variantMode = variants.some((variant) => Boolean(this.validateUrl(variant.url)));
      const candidate = variantMode ? selectedVariant?.url : data?.product?.url;

      return {
        mode: variantMode ? 'variant' : 'product',
        selectedVariant,
        destinationUrl: this.validateUrl(candidate),
      };
    }

    static validateCssLength(value) {
      if (typeof value !== 'string') return null;

      const normalized = value.trim();
      if (!normalized) return null;

      const invalidValues = new Set(['auto', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']);
      if (invalidValues.has(normalized.toLowerCase())) return null;

      if (typeof window.CSS?.supports === 'function') {
        if (!window.CSS.supports('height', normalized)) return null;
      } else {
        const lengthPattern =
          /^(?:0|(?:\d+|\d*\.\d+)(?:px|rem|em|vw|vh|vmin|vmax|ch|ex|cm|mm|q|in|pt|pc)|(?:calc|min|max|clamp)\(.+\))$/i;
        if (!lengthPattern.test(normalized)) return null;
      }

      return normalized;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.row = this.querySelector('.edifier-retailer-checkout-row');
      this.link = this.querySelector('.edifier-jb-hifi-button');
      this.data = this.parseData();
      this.productForm = document.getElementById(this.dataset.productFormId) || this.closest('form');
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);

      if (!this.row || !this.link || !this.data?.enabled) {
        this.hideButton();
        return;
      }

      this.productForm?.addEventListener('variant:change', this.handleVariantChange);
      this.link.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      this.updateForVariant({ id: this.data.selected_variant_id });
      this.scheduleHeightSync();
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener('variant:change', this.handleVariantChange);
      this.link?.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      if (this.heightSyncFrame) cancelAnimationFrame(this.heightSyncFrame);
      this.heightSyncFrame = null;
      this.initialized = false;
    }

    parseData() {
      try {
        return JSON.parse(this.querySelector('[data-edifier-jb-hifi-data]')?.textContent || '');
      } catch (_error) {
        return null;
      }
    }

    handleVariantChange(event) {
      this.updateForVariant(event.detail?.variant);
    }

    handleResize() {
      this.scheduleHeightSync();
    }

    handleSectionLoad(event) {
      if (event.target?.contains?.(this)) this.scheduleHeightSync();
    }

    scheduleHeightSync() {
      if (this.heightSyncFrame) return;

      this.heightSyncFrame = requestAnimationFrame(() => {
        this.heightSyncFrame = null;
        this.syncCheckoutButtonHeight();
      });
    }

    syncCheckoutButtonHeight() {
      const acceleratedCheckout = this.row?.querySelector('shopify-accelerated-checkout');
      if (!acceleratedCheckout) return;

      const sourceHeight = this.constructor.validateCssLength(
        getComputedStyle(acceleratedCheckout).getPropertyValue(
          '--shopify-accelerated-checkout-button-block-size',
        ),
      );
      if (!sourceHeight) return;

      const renderedButton = acceleratedCheckout.querySelector('[slot="button"]');
      const renderedHeight = renderedButton?.getBoundingClientRect().height;
      if (!Number.isFinite(renderedHeight) || renderedHeight <= 0) return;

      const computedHeight = `${renderedHeight}px`;
      if (computedHeight === this.checkoutButtonHeight) return;

      this.style.setProperty('--edifier-checkout-button-height', computedHeight);
      this.checkoutButtonHeight = computedHeight;
    }

    updateForVariant(variant) {
      const variantId = variant?.id;
      const resolved = this.constructor.resolveDestination(this.data, variantId);
      const storedVariant = resolved.selectedVariant;

      this.current = {
        variantId: storedVariant?.id ?? variantId ?? '',
        sku: storedVariant?.sku ?? variant?.sku ?? '',
        destinationUrl: resolved.destinationUrl,
      };

      if (!resolved.destinationUrl) {
        this.hideButton();
        return;
      }

      this.link.href = resolved.destinationUrl;
      this.link.hidden = false;
      this.row.dataset.retailerActive = 'true';
      this.row.dataset.initialCandidate = 'true';
    }

    hideButton() {
      if (!this.link || !this.row) return;
      this.link.hidden = true;
      this.link.removeAttribute('href');
      this.row.dataset.retailerActive = 'false';
      this.row.dataset.initialCandidate = 'false';
      this.current = null;
    }

    handleClick() {
      if (!this.current?.destinationUrl) return;

      try {
        const analytics = window.Shopify?.analytics;
        if (typeof analytics?.publish !== 'function') return;

        analytics.publish('edifier:jb_hifi_click', {
          product_id: this.data.product?.id ?? '',
          product_title: this.data.product?.title ?? '',
          product_handle: this.data.product?.handle ?? '',
          variant_id: this.current.variantId,
          sku: this.current.sku,
          destination_url: this.current.destinationUrl,
          page_url: window.location.href,
          market: 'AU',
          retailer: 'JB Hi-Fi',
          button_position: 'product_buy_area',
        });
      } catch (_error) {
        // Tracking must never interrupt retailer navigation.
      }
    }
  }

  customElements.define('edifier-retailer-button', EdifierRetailerButton);
})();
